import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { format, parseISO, isValid, endOfDay } from 'date-fns';
import { Observable, combineLatest, filter, map, startWith, takeWhile, tap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
    selector: 'app-date-time',
    templateUrl: 'date-time.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: DateTimeComponent,
        },
    ],
    imports: [MatFormFieldModule, MatInputModule, MatDatepickerModule, FormsModule, ReactiveFormsModule, TranslateModule],
})
export class DateTimeComponent implements ControlValueAccessor, OnInit, OnDestroy {
    readonly includeTime = input(true);
    readonly label = input.required<string>();
    readonly min = input<Date>(undefined);
    readonly max = input<Date>(undefined);
    readonly endOfDay = input(false);

    dateControl = new FormControl();
    timeControl = new FormControl();

    ALIVE: boolean;

    constructor() {}

    onChange = (_) => {};

    onTouched = () => {};

    touched = false;

    disabled = false;

    ngOnInit(): void {
        this.ALIVE = true;

        this._initListeners();
    }

    ngOnDestroy(): void {
        this.ALIVE = false;
    }

    writeValue(date: string): void {
        if (date) {
            const parsedDate = parseISO(date);
            this.dateControl.setValue(format(parsedDate, 'yyyy-MM-dd'));
            this.timeControl.setValue(format(parsedDate, 'HH:mm'));
        }
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.dateControl.disable({ emitEvent: false });
            this.timeControl.disable({ emitEvent: false });
        } else {
            this.dateControl.enable({ emitEvent: false });
            this.timeControl.enable({ emitEvent: false });
        }
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private _initListeners() {
        const date$: Observable<Date> = this.dateControl.valueChanges.pipe(startWith(this.dateControl.value));
        const time$: Observable<string> = this.timeControl.valueChanges.pipe(startWith(this.timeControl.value));

        combineLatest([date$, time$])
            .pipe(
                takeWhile(() => this.ALIVE),
                tap(([date, _]) => {
                    this.markAsTouched();
                    if (!date) {
                        this.onChange(null);
                    }
                }),
                filter(([date, _]) => date != null),
                map(([date, time]) => {
                    const dateStr = date instanceof Date ? format(date, 'yyyy-MM-dd') : String(date);
                    const dateTimeStr = dateStr + (this.includeTime() ? ` ${time ?? '00:00'}` : ' 00:00');
                    let dateAndTime = parseISO(dateTimeStr.replace(' ', 'T'));
                    if (!this.includeTime() && this.endOfDay()) {
                        dateAndTime = endOfDay(dateAndTime);
                    }
                    return dateAndTime;
                }),
                filter((dateTime) => isValid(dateTime)),
                map((dateTime) => format(dateTime, this.includeTime() || this.endOfDay() ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd')),
                tap((dateTime) => {
                    this.onChange(dateTime);
                }),
            )
            .subscribe();
    }
}
